import { useEffect, useRef, useState } from "react";
import { ArrowLeft, CheckCircle, LoaderCircle, LockKeyhole, Mail, RotateCcw } from "lucide-react";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useGuestOtpAuth } from "@/hooks/useGuestOtpAuth";

const OTP_LENGTH = 6;

interface MatchingOtpGateProps {
  onAuthenticated: () => void;
  onBack: () => void;
  busy?: boolean;
}

export function MatchingOtpGate({ onAuthenticated, onBack, busy = false }: MatchingOtpGateProps) {
  const otp = useGuestOtpAuth(onAuthenticated);
  const [code, setCode] = useState("");
  const autoSubmittedCodeRef = useRef("");

  const collecting = otp.phase === "collect" || otp.phase === "sending";
  const sending = otp.phase === "sending";
  const verifying = otp.phase === "verifying";
  const canSend = /\S+@\S+\.\S+/.test(otp.email);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (otp.phase === "code" && otp.errorKey) {
      setCode("");
    }
  }, [otp.phase, otp.errorKey]);

  useEffect(() => {
    if (code.length < OTP_LENGTH) {
      autoSubmittedCodeRef.current = "";
      return;
    }
    if (
      otp.phase !== "code" ||
      verifying ||
      busy ||
      autoSubmittedCodeRef.current === code
    ) {
      return;
    }
    autoSubmittedCodeRef.current = code;
    void otp.verifyCode(code);
  }, [otp, code, verifying, busy]);

  const resendText =
    otp.cooldown > 0 ? `Pošalji ponovo · 0:${String(otp.cooldown).padStart(2, "0")}` : "Pošalji ponovo";

  return (
    <main className="min-h-[calc(100svh-3.5rem)] bg-[oklch(95.43%_0.0136_78.26)] p-0 text-foreground sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100svh-3.5rem)] max-w-2xl items-stretch sm:min-h-0 sm:items-center">
        <section className="flex min-h-[calc(100svh-3.5rem)] w-full flex-col justify-between bg-white px-5 py-6 shadow-[0_24px_70px_-46px_rgba(41,35,27,0.55)] sm:min-h-0 sm:rounded-[2rem] sm:border sm:border-[oklch(84%_0.03_84)] sm:p-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[oklch(80%_0.035_84)] bg-[oklch(98%_0.005_84)] px-4 py-2 text-sm font-semibold text-[oklch(37.12%_0.059_186.62)]">
              <LockKeyhole className="size-4" />
              Još jedan kratki korak
            </div>

            <div className="mt-8 space-y-4">
              <h1 className="text-balance text-3xl font-semibold leading-tight tracking-normal text-[oklch(25%_0.04_84)] sm:text-4xl">
                Potvrdite email da vidite preporuke
              </h1>
              <p className="text-base leading-7 text-[oklch(42%_0.035_84)]">
                Sačuvat ćemo vaš odgovor i kreirati nalog bez šifre. Nakon koda iz emaila odmah
                prikazujemo dva preporučena terapeuta.
              </p>
            </div>

            {collecting ? (
              <div className="mt-7 space-y-4">
                <label htmlFor="matching-otp-email" className="block text-sm font-semibold text-[oklch(25%_0.04_84)]">
                  Email adresa
                </label>
                <input
                  id="matching-otp-email"
                  type="email"
                  value={otp.email}
                  onChange={(event) => otp.setEmail(event.target.value)}
                  placeholder="vas@email.com"
                  autoComplete="email"
                  className="h-13 w-full rounded-2xl border border-[oklch(86%_0.025_84)] bg-white px-4 text-base outline-none transition placeholder:text-muted-foreground/55 focus:border-[oklch(37.12%_0.059_186.62)] focus:ring-[3px] focus:ring-[oklch(37.12%_0.059_186.62)]/15"
                />
                {otp.errorKey ? <p className="text-sm text-destructive">{otpErrorText(otp.errorKey)}</p> : null}
              </div>
            ) : (
              <div className="mt-7 space-y-5">
                <div className="rounded-2xl border border-[oklch(86%_0.025_84)] bg-[oklch(98%_0.005_84)] p-4 text-center">
                  <p className="text-sm text-muted-foreground">Poslali smo 6-cifreni kod na</p>
                  <p className="mt-1 font-semibold text-[oklch(25%_0.04_84)]">{otp.email}</p>
                </div>

                <OtpCodeInput
                  id="matching-otp"
                  value={code}
                  onChange={setCode}
                  invalid={!!otp.errorKey}
                  autoFocus
                  className="mx-auto max-w-[22rem]"
                  inputClassName="bg-white focus:border-[oklch(37.12%_0.059_186.62)] focus:ring-[oklch(37.12%_0.059_186.62)]/15"
                />

                {otp.errorKey ? <p className="text-center text-sm text-destructive">{otpErrorText(otp.errorKey)}</p> : null}

                <p className="text-center text-sm text-muted-foreground">
                  Niste dobili kod?{" "}
                  <button
                    type="button"
                    onClick={otp.resend}
                    disabled={otp.cooldown > 0}
                    className={cn(
                      "font-semibold",
                      otp.cooldown > 0 ? "text-muted-foreground" : "text-[oklch(37.12%_0.059_186.62)] underline",
                    )}
                  >
                    {resendText}
                  </button>
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 space-y-3">
            {collecting ? (
              <Button
                type="button"
                onClick={otp.sendCode}
                disabled={!canSend || sending}
                className="h-13 w-full rounded-full bg-[oklch(37.12%_0.059_186.62)] px-6 text-base font-semibold text-white shadow-[0_18px_36px_-22px_rgba(8,45,42,0.8)] hover:bg-[oklch(29.52%_0.0501_186.62)]"
              >
                {sending ? <LoaderCircle className="size-5 animate-spin" /> : <Mail className="size-5" />}
                {sending ? "Šaljem kod" : "Pošalji kod"}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => otp.verifyCode(code)}
                disabled={code.length < OTP_LENGTH || verifying || busy}
                className="h-13 w-full rounded-full bg-[oklch(37.12%_0.059_186.62)] px-6 text-base font-semibold text-white shadow-[0_18px_36px_-22px_rgba(8,45,42,0.8)] hover:bg-[oklch(29.52%_0.0501_186.62)]"
              >
                {verifying || busy ? <LoaderCircle className="size-5 animate-spin" /> : <CheckCircle className="size-5" />}
                {verifying || busy ? "Provjeravam" : "Prikaži preporuke"}
              </Button>
            )}

            <button
              type="button"
              onClick={collecting ? onBack : () => {
                setCode("");
                otp.editEmail();
              }}
              className="mx-auto flex h-10 items-center justify-center gap-2 px-4 text-sm font-medium text-muted-foreground"
            >
              {collecting ? <ArrowLeft className="size-4" /> : <RotateCcw className="size-4" />}
              {collecting ? "Nazad na upitnik" : "Promijeni email"}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}

function otpErrorText(key: string): string {
  if (key.includes("Expired")) return "Kod je istekao. Zatražite novi.";
  if (key.includes("Attempts")) return "Previše pokušaja. Zatražite novi kod.";
  if (key.includes("Wrong")) return "Netačan kod. Pokušajte ponovo.";
  if (key.includes("Send")) return "Slanje koda nije uspjelo. Pokušajte ponovo.";
  return "Nešto je pošlo po zlu. Pokušajte ponovo.";
}
